import { createSelector } from "reselect"
import { ReduxState, RateTable, NestedLaborRate } from "../common/types"
import { SerializedRateTable, SerializedLaborRate } from "@rhumbix/rmbx_matching"
import { CustomFieldCriterion } from "@rhumbix/rmbx_work_rules"
import { tEntitySelection } from "../cached-data/types"

/**
 * Selects the rate tables entity state from Redux.
 */
const selectRateTablesById = (state: ReduxState): tEntitySelection["rateTables"] =>
    state.entities.rateTables?.objects ?? {}

/**
 * Selector to get the cached labor rates from Redux state.
 */
export const selectLaborRatesById = (state: ReduxState): tEntitySelection["laborRates"] =>
    state.entities.laborRates?.objects ?? {}

/**
 * Transforms a LaborRate object from a cached RateTable's labor_rates field into the format
 * expected by the matching library.
 *
 * The custom_fields transformation maps from the Redux structure (Record<string, string[]>)
 * to a flat CustomFieldCriterion[] structure expected by the matching library.
 * Each field name+value pair becomes a criterion with a simple "equals" condition for matching.
 */
const transformLaborRateForMatching = (laborRate: NestedLaborRate): SerializedLaborRate => {
    // Build modifier_custom_ids from custom_modifier_* fields
    const modifierCustomIds: Record<string, number[]> = {}

    // Extract any custom_modifier_* fields
    Object.entries(laborRate).forEach(([key, value]) => {
        if (key.startsWith("custom_modifier_") && Array.isArray(value) && value.length > 0) {
            const modifierName = key.replace("custom_modifier_", "")
            modifierCustomIds[modifierName] = value as number[]
        }
    })

    // Transform custom_fields from Record<string, string[]> to CustomFieldCriterion[]
    // Each entry in the record becomes one criterion per value with "equals" condition
    const customFieldsCriteria: CustomFieldCriterion[] = []
    if (laborRate.custom_fields) {
        const categoryByFieldName: Record<string, string> = {
            employee: "Worker",
            cost_code: "Cost Code",
            project: "Project",
        }
        Object.entries(laborRate.custom_fields).forEach(([fieldName, values]) => {
            const category = categoryByFieldName[fieldName]
            values.forEach((value, index) => {
                if (!category) {
                    return
                }
                customFieldsCriteria.push({
                    id: index,
                    category: category as "Worker" | "Cost Code" | "Project",
                    name: fieldName,
                    condition: "equals",
                    value: value,
                })
            })
        })
    }

    return {
        id: laborRate.id,
        row_order: laborRate.row_order,
        name: laborRate.name,
        time_type: laborRate.time_type,
        base_rate: laborRate.base_rate,
        custom_rates: laborRate.custom_rates ?? undefined,
        trades: laborRate.trades ?? [],
        classifications: laborRate.classifications ?? [],
        modifier_equipment_ids: laborRate.equipment_modifiers,
        modifier_custom_ids: Object.keys(modifierCustomIds).length > 0 ? modifierCustomIds : undefined,
        union_ids: laborRate.unions ?? [],
        cohort_ids: laborRate.cohorts ?? [],
        custom_fields: customFieldsCriteria,
        certification_ids: laborRate.certifications,
        license_ids: laborRate.licenses,
        project_client_names: [], // TODO: WA-9210 add Project - Client Name to match criteria
    }
}

/**
 * Transforms a cached RateTable from Redux into the format expected by the matching library.
 */
const transformRateTableForMatching = (rateTable: RateTable): SerializedRateTable => {
    return {
        id: rateTable.id,
        name: rateTable.name,
        version: rateTable.version,
        labor_rates: rateTable.labor_rates?.map(transformLaborRateForMatching) ?? [],
    }
}

/**
 * Returns a map of rate table IDs to serialized rate tables, like this:
 * {
 *    <id>: <SerializedRateTable>
 * }
 */
export const selectRateTablesForMatchingById = createSelector(
    [selectRateTablesById],
    (rateTables): Partial<Record<number, SerializedRateTable>> => {
        return Object.values(rateTables).reduce((acc, rateTable) => {
            acc[rateTable.id] = transformRateTableForMatching(rateTable)
            return acc
        }, {} as Partial<Record<number, SerializedRateTable>>)
    }
)
